import {
    ChatIcon,
    ClockIcon,
    CrossIcon,
    DownloadIcon,
    FileIcon,
    PlusIcon,
    SearchIcon,
    SendIcon,
    UploadIcon,
    UserPlusIcon,
} from '@icon/index';
import { Drawer } from '@library/drawer';
import { CircularProgress } from '@library/loaders/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DragEvent, FormEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import {
    CreateInternalSupportTicketPayload,
    InternalSupportActivity,
    InternalSupportMessage,
    InternalSupportTicket,
    InternalSupportUser,
    TicketAttachment,
    ticketPriorities,
    TicketPriority,
    TicketStatus,
    ticketStatuses,
    UpdateInternalSupportTicketPayload,
} from '../../helpers/internal-support.types';
import useAssignableUsers from '../../hooks/useAssignableUsers';
import useInternalSupportTicketSocket from '../../hooks/useInternalSupportTicketSocket';
import useInternalSupportUsers from '../../hooks/useInternalSupportUsers';
import useIsMobile from '../../hooks/useIsMobile';
import {
    addPulseInternalTicketAssignee,
    createPulseInternalTicket,
    createPulseInternalTicketMessage,
    getPulseInternalTicketActivity,
    getPulseInternalTicketById,
    getPulseInternalTicketMessages,
    getPulseInternalTickets,
    removePulseInternalTicketAssignee,
    updatePulseInternalTicket,
    updatePulseInternalTicketPriority,
    updatePulseInternalTicketStatus,
} from '../../services/internal-support.service';

type TicketTab = 'conversation' | 'notes' | 'activity' | 'attachments';
type TicketListTab = 'all' | 'mine' | 'unassigned';

const emptyTicketForm: CreateInternalSupportTicketPayload = {
    title: '',
    description: '',
    priority: 'MEDIUM',
    assignees: [],
    dueDate: '',
    status: 'OPEN',
    attachmentFiles: [],
};

const formatStatus = (status: TicketStatus) =>
    ({ OPEN: 'Open', IN_PROGRESS: 'In Progress', WAITING: 'Waiting', RESOLVED: 'Resolved', CLOSED: 'Closed' })[status];

const formatPriority = (priority: TicketPriority) =>
    ({ LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', URGENT: 'Urgent' })[priority];

const formatDateTime = (value?: string) =>
    value
        ? new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
        : 'Not set';

const formatShortDate = (value?: string) =>
    value ? new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' }).format(new Date(value)) : 'Not set';

const formatFileSize = (size?: number) => {
    if (!size) return 'Unknown size';
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const priorityClass: Record<TicketPriority, string> = {
    LOW: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    MEDIUM: 'border-amber-200 bg-amber-50 text-amber-700',
    HIGH: 'border-rose-200 bg-rose-50 text-rose-700',
    URGENT: 'border-red-200 bg-red-600 text-white',
};

const statusClass: Record<TicketStatus, string> = {
    OPEN: 'border-sky-200 bg-sky-50 text-sky-700',
    IN_PROGRESS: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    WAITING: 'border-amber-200 bg-amber-50 text-amber-700',
    RESOLVED: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    CLOSED: 'border-grayscale-300 bg-grayscale-100 text-grayscale-700',
};

const getApiMessage = (err: any) => err?.data?.message || err?.response?.data?.message || 'Something went wrong.';

const getTicketUserPool = (
    tickets: InternalSupportTicket[],
    messages: InternalSupportMessage[],
    activities: InternalSupportActivity[],
    selectedTicket?: InternalSupportTicket
) => {
    const userMap = new Map<string, InternalSupportUser>();
    const addUser = (user?: InternalSupportUser) => {
        if (user?._id) userMap.set(user._id, user);
    };

    tickets.forEach((ticket) => {
        addUser(ticket.createdBy);
        ticket.assignees?.forEach(addUser);
        addUser(ticket.closedBy);
    });
    addUser(selectedTicket?.createdBy);
    selectedTicket?.assignees?.forEach(addUser);
    messages.forEach((message) => {
        addUser(message.sender);
        message.mentions?.forEach(addUser);
    });
    activities.forEach((activity) => addUser(activity.performedBy));

    return Array.from(userMap.values());
};

const InternalSupport = () => {
    const { info: currentUser } = useSelector((state: any) => state.profile);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { ticketId: urlTicketId } = useParams<{ ticketId?: string }>();
    const isMobile = useIsMobile();
    const [listTab, setListTab] = useState<TicketListTab>('all');
    const [activeTab, setActiveTab] = useState<TicketTab>('conversation');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
    const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
    const [composerValue, setComposerValue] = useState('');
    const [composerAttachments, setComposerAttachments] = useState<File[]>([]);
    const [mentionedUsers, setMentionedUsers] = useState<InternalSupportUser[]>([]);
    const [mentionQuery, setMentionQuery] = useState<string | null>(null);
    const [mentionDropdownOpen, setMentionDropdownOpen] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [newTicket, setNewTicket] = useState<CreateInternalSupportTicketPayload>(emptyTicketForm);
    const [editTicket, setEditTicket] = useState<UpdateInternalSupportTicketPayload>({});

    const listQueryParams = useMemo(
        () => ({
            search: search.trim() || undefined,
            status: statusFilter || undefined,
            priority: priorityFilter || undefined,
            assignedToMe: listTab === 'mine' || undefined,
            unassigned: listTab === 'unassigned' || undefined,
        }),
        [listTab, priorityFilter, search, statusFilter]
    );

    const ticketsQuery = useQuery(
        ['pulse-internal-tickets', listQueryParams],
        () => getPulseInternalTickets(listQueryParams),
        { keepPreviousData: true }
    );

    const usersQuery = useInternalSupportUsers();
    const assignableUsersQuery = useAssignableUsers();

    const tickets = ticketsQuery.data?.tickets || [];
    const selectedListTicket =
        tickets.find((ticket) => ticket.ticketId === urlTicketId) || (!isMobile ? tickets[0] : undefined);
    const effectiveTicketId = urlTicketId || selectedListTicket?.ticketId;

    const detailQuery = useQuery(
        ['pulse-internal-ticket', effectiveTicketId],
        () => getPulseInternalTicketById(effectiveTicketId),
        { enabled: Boolean(effectiveTicketId) }
    );

    const messagesQuery = useQuery(
        ['pulse-internal-ticket-messages', effectiveTicketId],
        () => getPulseInternalTicketMessages(effectiveTicketId),
        { enabled: Boolean(effectiveTicketId) }
    );

    const activityQuery = useQuery(
        ['pulse-internal-ticket-activity', effectiveTicketId],
        () => getPulseInternalTicketActivity(effectiveTicketId),
        { enabled: Boolean(effectiveTicketId) }
    );

    const selectedTicket = detailQuery.data || selectedListTicket;
    const messages = messagesQuery.data?.messages || [];
    const activities = activityQuery.data?.activities || [];
    const assignableUsers = assignableUsersQuery.data || [];

    const users = useMemo(() => {
        const apiUsers = usersQuery.data || [];
        const ticketUsers = getTicketUserPool(tickets, messages, activities, selectedTicket);
        const userMap = new Map<string, InternalSupportUser>();

        [...apiUsers, ...ticketUsers].forEach((user) => {
            if (user?._id) userMap.set(user._id, user);
        });

        return Array.from(userMap.values());
    }, [activities, messages, selectedTicket, tickets, usersQuery.data]);

    // Enrich assignees with full details from assignableUsers
    const enrichedAssignees = useMemo(() => {
        if (!selectedTicket?.assignees) return [];

        const assignableUserMap = new Map(assignableUsers.map((u) => [u._id, u]));

        return selectedTicket.assignees.map((assignee) => {
            const fullDetails = assignableUserMap.get(assignee._id);
            return fullDetails || assignee;
        });
    }, [selectedTicket?.assignees, assignableUsers]);

    const handleSocketMessage = useCallback(
        (message: InternalSupportMessage) => {
            messagesQuery.refetch();
            activityQuery.refetch();
            ticketsQuery.refetch();
        },
        [messagesQuery, activityQuery, ticketsQuery]
    );

    const handleSocketTicketUpdated = useCallback(
        (ticket: InternalSupportTicket) => {
            detailQuery.refetch();
            ticketsQuery.refetch();
            activityQuery.refetch();
        },
        [detailQuery, ticketsQuery, activityQuery]
    );

    useInternalSupportTicketSocket({
        ticketId: effectiveTicketId,
        onMessage: handleSocketMessage,
        onTicketUpdated: handleSocketTicketUpdated,
    });

    const refreshSelectedTicket = async () => {
        await Promise.all([
            ticketsQuery.refetch(),
            detailQuery.refetch(),
            messagesQuery.refetch(),
            activityQuery.refetch(),
        ]);
    };

    const createTicketMutation = useMutation(createPulseInternalTicket, {
        onSuccess: async (ticket) => {
            navigate(`/internal-support/${ticket.ticketId}`);
            setNewTicket({ ...emptyTicketForm });
            setIsCreateOpen(false);
            await ticketsQuery.refetch();
            toast.success('Ticket created');
        },
        onError: (err) => toast.error(getApiMessage(err)),
    });

    const updateDetailsMutation = useMutation(
        ({ ticketId, payload }: { ticketId: string; payload: UpdateInternalSupportTicketPayload }) =>
            updatePulseInternalTicket(ticketId, payload),
        {
            onSuccess: async () => {
                setIsEditOpen(false);
                await refreshSelectedTicket();
                toast.success('Ticket updated');
            },
            onError: (err) => toast.error(getApiMessage(err)),
        }
    );

    const updateStatusMutation = useMutation(
        ({ ticketId, status }: { ticketId: string; status: TicketStatus }) =>
            updatePulseInternalTicketStatus(ticketId, status),
        {
            onMutate: async ({ ticketId, status }) => {
                await queryClient.cancelQueries(['pulse-internal-ticket', ticketId]);
                const previous = queryClient.getQueryData(['pulse-internal-ticket', ticketId]);
                queryClient.setQueryData(['pulse-internal-ticket', ticketId], (old: any) => ({
                    ...old,
                    status,
                }));
                return { previous, ticketId };
            },
            onError: (err, _, context: any) => {
                if (context?.previous) {
                    queryClient.setQueryData(['pulse-internal-ticket', context.ticketId], context.previous);
                }
                toast.error(getApiMessage(err));
            },
            onSuccess: () => {
                queryClient.invalidateQueries(['pulse-internal-tickets']);
                queryClient.invalidateQueries(['pulse-internal-ticket-activity']);
            },
        }
    );

    const updatePriorityMutation = useMutation(
        ({ ticketId, priority }: { ticketId: string; priority: TicketPriority }) =>
            updatePulseInternalTicketPriority(ticketId, priority),
        {
            onMutate: async ({ ticketId, priority }) => {
                await queryClient.cancelQueries(['pulse-internal-ticket', ticketId]);
                const previous = queryClient.getQueryData(['pulse-internal-ticket', ticketId]);
                queryClient.setQueryData(['pulse-internal-ticket', ticketId], (old: any) => ({
                    ...old,
                    priority,
                }));
                return { previous, ticketId };
            },
            onError: (err, _, context: any) => {
                if (context?.previous) {
                    queryClient.setQueryData(['pulse-internal-ticket', context.ticketId], context.previous);
                }
                toast.error(getApiMessage(err));
            },
            onSuccess: () => {
                queryClient.invalidateQueries(['pulse-internal-tickets']);
                queryClient.invalidateQueries(['pulse-internal-ticket-activity']);
            },
        }
    );

    const addAssigneeMutation = useMutation(
        ({ ticketId, userId }: { ticketId: string; userId: string }) =>
            addPulseInternalTicketAssignee(ticketId, userId),
        {
            onMutate: async ({ ticketId, userId }) => {
                await queryClient.cancelQueries(['pulse-internal-ticket', ticketId]);
                const previous = queryClient.getQueryData(['pulse-internal-ticket', ticketId]);
                queryClient.setQueryData(['pulse-internal-ticket', ticketId], (old: any) => ({
                    ...old,
                    assignees: [...(old?.assignees || []), { _id: userId }],
                }));
                return { previous, ticketId };
            },
            onError: (err, _, context: any) => {
                if (context?.previous) {
                    queryClient.setQueryData(['pulse-internal-ticket', context.ticketId], context.previous);
                }
                toast.error(getApiMessage(err));
            },
            onSuccess: () => {
                queryClient.invalidateQueries(['pulse-internal-ticket-activity']);
            },
        }
    );

    const removeAssigneeMutation = useMutation(
        ({ ticketId, userId }: { ticketId: string; userId: string }) =>
            removePulseInternalTicketAssignee(ticketId, userId),
        {
            onMutate: async ({ ticketId, userId }) => {
                await queryClient.cancelQueries(['pulse-internal-ticket', ticketId]);
                const previous = queryClient.getQueryData(['pulse-internal-ticket', ticketId]);
                queryClient.setQueryData(['pulse-internal-ticket', ticketId], (old: any) => ({
                    ...old,
                    assignees: old?.assignees?.filter((a: any) => a._id !== userId) || [],
                }));
                return { previous, ticketId };
            },
            onError: (err, _, context: any) => {
                if (context?.previous) {
                    queryClient.setQueryData(['pulse-internal-ticket', context.ticketId], context.previous);
                }
                toast.error(getApiMessage(err));
            },
            onSuccess: () => {
                queryClient.invalidateQueries(['pulse-internal-ticket-activity']);
            },
        }
    );

    const addMessageMutation = useMutation(
        ({
            ticketId,
            message,
            mentions,
            attachmentFiles,
        }: {
            ticketId: string;
            message?: string;
            mentions: string[];
            attachmentFiles: File[];
        }) => createPulseInternalTicketMessage(ticketId, { message, type: 'MESSAGE', mentions, attachmentFiles }),
        {
            onSuccess: async () => {
                setComposerValue('');
                setComposerAttachments([]);
                setMentionedUsers([]);
                await refreshSelectedTicket();
                toast.success('Reply sent');
            },
            onError: (err) => toast.error(getApiMessage(err)),
        }
    );

    const conversationMessages = messages.filter((message) => message.type === 'MESSAGE');
    const allAttachments = [
        ...(selectedTicket?.attachments || []),
        ...messages.flatMap((message) => message.attachments || []),
    ];
    const availableAssignees = assignableUsers.filter(
        (user) => !selectedTicket?.assignees?.some((assignee) => assignee._id === user._id)
    );

    const handleSelectTicket = (ticket: InternalSupportTicket) => {
        navigate(`/internal-support/${ticket.ticketId}`);
        setActiveTab('conversation');
    };

    const handleSendMessage = (event: FormEvent) => {
        event.preventDefault();
        if (!selectedTicket || (!composerValue.trim() && composerAttachments.length === 0)) return;

        const mentions = mentionedUsers
            .filter((u) => composerValue.includes('@' + (u.username || u.name)))
            .map((u) => u._id);

        addMessageMutation.mutate({
            ticketId: selectedTicket.ticketId,
            message: composerValue.trim() || undefined,
            mentions,
            attachmentFiles: composerAttachments,
        });
    };

    const handleComposerFilesSelected = (files: File[]) => {
        setComposerAttachments((prev) => [...prev, ...files]);
    };

    const handleRemoveComposerAttachment = (index: number) => {
        setComposerAttachments((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    };

    const handleCreateTicket = (event: FormEvent) => {
        event.preventDefault();
        if (!newTicket.title.trim()) {
            toast.error('Title is required');
            return;
        }

        createTicketMutation.mutate({
            ...newTicket,
            title: newTicket.title.trim(),
            description: newTicket.description?.trim() || '',
            dueDate: newTicket.dueDate ? new Date(newTicket.dueDate).toISOString() : undefined,
            assignees: newTicket.assignees?.filter(Boolean),
            attachmentFiles: newTicket.attachmentFiles || [],
        });
    };

    const handleEditTicket = (event: FormEvent) => {
        event.preventDefault();
        if (!selectedTicket) return;

        const { status, assignees, ...detailsPayload } = editTicket;
        const currentAssigneeIds = selectedTicket.assignees?.map((a) => a._id) || [];
        const newAssigneeIds = assignees || [];

        updateDetailsMutation.mutate({
            ticketId: selectedTicket.ticketId,
            payload: {
                ...detailsPayload,
                title: detailsPayload.title?.trim(),
                description: detailsPayload.description?.trim(),
                dueDate: detailsPayload.dueDate || undefined,
            },
        });

        if (status && status !== selectedTicket.status) {
            updateStatusMutation.mutate({
                ticketId: selectedTicket.ticketId,
                status,
            });
        }

        // Handle assignee changes
        const assigneesToAdd = newAssigneeIds.filter((id) => !currentAssigneeIds.includes(id));
        const assigneesToRemove = currentAssigneeIds.filter((id) => !newAssigneeIds.includes(id));

        assigneesToAdd.forEach((userId) => {
            addAssigneeMutation.mutate({
                ticketId: selectedTicket.ticketId,
                userId,
            });
        });

        assigneesToRemove.forEach((userId) => {
            removeAssigneeMutation.mutate({
                ticketId: selectedTicket.ticketId,
                userId,
            });
        });
    };

    const openEditDrawer = () => {
        if (!selectedTicket) return;

        setEditTicket({
            title: selectedTicket.title,
            description: selectedTicket.description || '',
            status: selectedTicket.status,
            priority: selectedTicket.priority,
            assignees: selectedTicket.assignees?.map((a) => a._id) || [],
            dueDate: selectedTicket.dueDate?.slice(0, 10) || '',
        });
        setIsEditOpen(true);
    };

    const handleComposerChange = (value: string) => {
        setComposerValue(value);

        const caret = value.length;
        const lastAtIndex = value.lastIndexOf('@', caret - 1);
        if (lastAtIndex === -1) {
            setMentionQuery(null);
            setMentionDropdownOpen(false);
            return;
        }

        const afterAt = value.substring(lastAtIndex + 1, caret);
        if (afterAt.includes(' ') || afterAt.includes('\n')) {
            setMentionQuery(null);
            setMentionDropdownOpen(false);
            return;
        }

        setMentionQuery(afterAt);
        setMentionDropdownOpen(afterAt.length > 0);
    };

    const handleMentionSelect = (user: InternalSupportUser) => {
        const displayName = user.username || user.name;
        const lastAtIndex = composerValue.lastIndexOf('@');
        const beforeAt = composerValue.substring(0, lastAtIndex);
        const newValue = beforeAt + '@' + displayName + ' ';
        setComposerValue(newValue);
        setMentionedUsers([...mentionedUsers, user]);
        setMentionQuery(null);
        setMentionDropdownOpen(false);
    };

    const filteredMentionUsers =
        mentionQuery && mentionDropdownOpen
            ? assignableUsers.filter(
                  (u) =>
                      !mentionedUsers.some((mu) => mu._id === u._id) &&
                      (u.name?.toLowerCase().includes(mentionQuery.toLowerCase()) ||
                          (u.username || '').toLowerCase().includes(mentionQuery.toLowerCase()))
              )
            : [];

    if (ticketsQuery.isLoading && !ticketsQuery.data) {
        return (
            <div className="grid h-screen place-items-center">
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100dvh-var(--navbar-height))] min-h-0 flex-col bg-grayscale-100 xl:flex-row">
            {!(isMobile && effectiveTicketId) && (
                <aside className="flex w-full shrink-0 flex-col border-b border-r-0 border-separator-color bg-white xl:w-[340px] xl:border-b-0 xl:border-r xl:max-h-screen">
                    <div className="border-b border-separator-color p-3 sm:p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-xl font-semibold text-grayscale-900">Internal Support</h3>
                                <p className="text-xs text-grayscale-500">{ticketsQuery.data?.total || 0} tickets</p>
                            </div>
                            <button
                                type="button"
                                title="New Ticket"
                                onClick={() => setIsCreateOpen(true)}
                                className="grid h-10 w-10 place-items-center rounded bg-primary text-white"
                            >
                                <PlusIcon />
                            </button>
                        </div>

                        <div className="mt-4 grid grid-cols-3 rounded border border-separator-color bg-grayscale-100 p-1 text-xs font-semibold">
                            {(['all', 'mine', 'unassigned'] as TicketListTab[]).map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setListTab(tab)}
                                    className={twMerge(
                                        'h-8 rounded text-grayscale-600',
                                        listTab === tab && 'bg-white text-primary shadow-sm'
                                    )}
                                >
                                    {tab === 'all' ? 'All' : tab === 'mine' ? 'My Tickets' : 'Unassigned'}
                                </button>
                            ))}
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                            <label className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded border border-separator-color bg-white px-3">
                                <SearchIcon className="text-grayscale-500" />
                                <input
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Search tickets..."
                                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                                />
                            </label>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <select
                                value={statusFilter}
                                onChange={(event) => setStatusFilter(event.target.value as TicketStatus | '')}
                                className="h-9 rounded border border-separator-color bg-white px-2 text-xs font-semibold outline-none"
                            >
                                <option value="">All Status</option>
                                {ticketStatuses.map((status) => (
                                    <option key={status} value={status}>
                                        {formatStatus(status)}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={priorityFilter}
                                onChange={(event) => setPriorityFilter(event.target.value as TicketPriority | '')}
                                className="h-9 rounded border border-separator-color bg-white px-2 text-xs font-semibold outline-none"
                            >
                                <option value="">All Priority</option>
                                {ticketPriorities.map((priority) => (
                                    <option key={priority} value={priority}>
                                        {formatPriority(priority)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto p-3 xl:max-h-[calc(100vh-180px)]">
                        {!tickets.length && !ticketsQuery.isFetching && (
                            <div className="rounded border border-separator-color bg-white p-3 text-xs text-grayscale-500 sm:p-4 sm:text-sm">
                                No tickets found.
                            </div>
                        )}

                        {tickets.map((ticket) => {
                            const isActive = ticket.ticketId === selectedTicket?.ticketId;

                            return (
                                <button
                                    key={ticket.ticketId}
                                    type="button"
                                    onClick={() => handleSelectTicket(ticket)}
                                    className={twMerge(
                                        'mb-2 w-full rounded border p-2 text-left transition sm:p-3',
                                        isActive
                                            ? 'border-primary bg-primary/5 shadow-sm'
                                            : 'border-separator-color bg-white hover:border-primary/50'
                                    )}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs font-semibold text-grayscale-500">
                                            {ticket.ticketId}
                                        </span>
                                        <Pill className={priorityClass[ticket.priority]}>
                                            {formatPriority(ticket.priority)}
                                        </Pill>
                                    </div>
                                    <div className="mt-1 line-clamp-2 text-xs font-semibold text-grayscale-900 sm:mt-2 sm:text-sm">
                                        {ticket.title}
                                    </div>
                                    <div className="mt-1 truncate text-xs text-grayscale-500 sm:mt-2">
                                        Created by {ticket.createdBy?.name || 'Unknown'} ·{' '}
                                        {formatDateTime(ticket.createdAt)}
                                    </div>
                                    <div className="mt-2 flex items-center justify-between sm:mt-3">
                                        <Pill className={statusClass[ticket.status]}>
                                            {formatStatus(ticket.status)}
                                        </Pill>
                                        {/* <span className="flex items-center gap-1 text-xs font-semibold text-grayscale-500">
                                        <ChatIcon /> {ticket.assignees?.length || 0}
                                    </span> */}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </aside>
            )}

            {!isMobile || effectiveTicketId ? (
                <>
                    {!selectedTicket ? (
                        <main className="grid min-w-0 flex-1 place-items-center bg-white text-sm text-grayscale-500">
                            Select or create a ticket.
                        </main>
                    ) : (
                        <>
                            <main className="flex min-w-0 flex-1 flex-col ">
                                <header className="border-b border-separator-color px-3 py-4 sm:px-6">
                                    <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                                        <div className="text-sm font-semibold text-primary">
                                            {selectedTicket.ticketId}
                                        </div>

                                        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
                                            {isMobile && (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/internal-support')}
                                                    className="h-8 flex-1 rounded border border-separator-color bg-white px-3 text-sm font-semibold text-grayscale-700 sm:flex-none"
                                                >
                                                    Back
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setIsDetailsOpen(true)}
                                                className="h-8 flex-1 rounded border border-separator-color bg-white px-3 text-sm font-semibold text-grayscale-700 sm:flex-none xl:hidden"
                                            >
                                                Details
                                            </button>
                                            <button
                                                type="button"
                                                onClick={openEditDrawer}
                                                className="h-8 flex-1 rounded border border-separator-color bg-white px-3 text-sm font-semibold text-grayscale-700 sm:flex-none"
                                            >
                                                Edit
                                            </button>
                                            <select
                                                value={selectedTicket.status}
                                                onChange={(event) =>
                                                    updateStatusMutation.mutate({
                                                        ticketId: selectedTicket.ticketId,
                                                        status: event.target.value as TicketStatus,
                                                    })
                                                }
                                                className="h-8 flex-1 rounded border border-separator-color bg-white px-2 text-xs font-semibold text-grayscale-800 outline-none sm:flex-none sm:px-3 sm:text-sm"
                                            >
                                                {ticketStatuses.map((status) => (
                                                    <option key={status} value={status}>
                                                        {formatStatus(status)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </header>

                                <nav className="flex overflow-x-auto border-b border-separator-color px-3 sm:px-6">
                                    {[
                                        ['conversation', `Conversation ${conversationMessages.length}`],
                                        ['activity', `Activity ${activities.length}`],
                                        ['attachments', `Attachments ${allAttachments.length}`],
                                    ].map(([tab, label]) => (
                                        <button
                                            key={tab}
                                            type="button"
                                            onClick={() => setActiveTab(tab as TicketTab)}
                                            className={twMerge(
                                                'h-12 whitespace-nowrap border-b-2 px-3 text-xs font-semibold sm:px-4 sm:text-sm',
                                                activeTab === tab
                                                    ? 'border-primary text-primary'
                                                    : 'border-transparent text-grayscale-500 hover:text-grayscale-900'
                                            )}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </nav>

                                <section className="min-h-0 max-h-[70vh] flex-1 overflow-y-auto bg-grayscale-50 px-3 py-4 sm:px-6 sm:py-5">
                                    {(detailQuery.isLoading || messagesQuery.isLoading || activityQuery.isLoading) && (
                                        <div className="grid h-full place-items-center">
                                            <CircularProgress />
                                        </div>
                                    )}
                                    {activeTab === 'conversation' && !messagesQuery.isLoading && (
                                        <MessageList
                                            messages={conversationMessages}
                                            emptyText="No replies yet."
                                            currentUserId={currentUser?._id}
                                        />
                                    )}
                                    {activeTab === 'activity' && !activityQuery.isLoading && (
                                        <ActivityList activities={activities} />
                                    )}
                                    {activeTab === 'attachments' && (
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            {allAttachments.map((attachment) => (
                                                <AttachmentCard
                                                    key={`${attachment.name}-${attachment.url}`}
                                                    attachment={attachment}
                                                />
                                            ))}
                                            {!allAttachments.length && (
                                                <div className="col-span-1 rounded border border-separator-color bg-white p-4 text-xs text-grayscale-500 sm:col-span-2 sm:p-6 sm:text-sm">
                                                    No attachments yet.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </section>

                                <form
                                    onSubmit={handleSendMessage}
                                    className="border-t border-separator-color bg-white p-3 sm:p-4 "
                                >
                                    {composerAttachments.length > 0 && (
                                        <div className="mb-2 flex flex-wrap gap-2">
                                            {composerAttachments.map((file, index) => (
                                                <div
                                                    key={`${file.name}-${file.lastModified}-${index}`}
                                                    className="flex items-center gap-2 rounded bg-grayscale-100 px-2 py-1 text-xs"
                                                >
                                                    <FileIcon className="h-3 w-3 text-grayscale-600" />
                                                    <span className="truncate text-grayscale-700">{file.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveComposerAttachment(index)}
                                                        className="ml-1 text-grayscale-500 hover:text-grayscale-700"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="relative flex items-end gap-2 sm:gap-3">
                                        <div className="relative flex-1">
                                            <textarea
                                                value={composerValue}
                                                onChange={(event) => handleComposerChange(event.target.value)}
                                                placeholder="Type your message... (use @ to mention)"
                                                className="min-h-[76px] w-full resize-none rounded border border-separator-color bg-white p-2 text-sm outline-none focus:border-primary sm:p-3"
                                            />
                                            {mentionDropdownOpen && filteredMentionUsers.length > 0 && (
                                                <div className="absolute bottom-full left-0 mb-2 max-h-48 w-full overflow-y-auto rounded border border-separator-color bg-white shadow-lg">
                                                    {filteredMentionUsers.map((user) => (
                                                        <button
                                                            key={user._id}
                                                            type="button"
                                                            onClick={() => handleMentionSelect(user)}
                                                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-grayscale-100"
                                                        >
                                                            <AvatarInitial user={user} size="sm" />
                                                            <div className="min-w-0">
                                                                <div className="truncate font-semibold text-grayscale-800">
                                                                    {user.name}
                                                                </div>
                                                                <div className="truncate text-xs text-grayscale-500">
                                                                    {user.username}
                                                                </div>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <label className="grid h-11 w-11 cursor-pointer place-items-center rounded border border-separator-color bg-white text-grayscale-600 hover:text-primary">
                                            <input
                                                type="file"
                                                multiple
                                                className="hidden"
                                                onChange={(event) => {
                                                    const files = Array.from(event.target.files || []);
                                                    handleComposerFilesSelected(files);
                                                    event.target.value = '';
                                                }}
                                                title="Attach files"
                                            />
                                            <UploadIcon />
                                        </label>
                                        <button
                                            type="submit"
                                            title="Send"
                                            disabled={
                                                (!composerValue.trim() && composerAttachments.length === 0) ||
                                                addMessageMutation.isLoading
                                            }
                                            className="grid h-11 w-11 place-items-center rounded bg-primary text-white disabled:opacity-50"
                                        >
                                            {addMessageMutation.isLoading ? (
                                                <CircularProgress size={18} className="text-white" />
                                            ) : (
                                                <SendIcon />
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </main>

                            <aside className="hidden w-full shrink-0 overflow-y-auto border-l border-separator-color bg-white p-3 sm:p-4 xl:block xl:w-[320px] xl:max-h-screen">
                                <DetailSection title="Details">
                                    <DetailRow label="Ticket ID" value={selectedTicket.ticketId} />
                                    <DetailRow label="Title" value={selectedTicket.title} />
                                    <DetailRow label="Description" value={selectedTicket.description} />
                                    <div>
                                        <div className="text-xs font-semibold text-grayscale-500">Status</div>
                                        <div className="mt-1">
                                            <Pill className={statusClass[selectedTicket.status]}>
                                                {formatStatus(selectedTicket.status)}
                                            </Pill>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold text-grayscale-500">Priority</div>
                                        <div className="mt-1">
                                            <Pill className={priorityClass[selectedTicket.priority]}>
                                                {formatPriority(selectedTicket.priority)}
                                            </Pill>
                                        </div>
                                    </div>
                                    <DetailRow label="Created by" value={selectedTicket.createdBy?.name || 'Unknown'} />
                                    <DetailRow label="Due Date" value={formatDateTime(selectedTicket.dueDate)} />
                                    <DetailRow label="Created" value={formatDateTime(selectedTicket.createdAt)} />
                                    <DetailRow label="Last updated" value={formatDateTime(selectedTicket.updatedAt)} />
                                </DetailSection>

                                <DetailSection title="Assignees">
                                    {enrichedAssignees.map((user) => (
                                        <div key={user._id} className="flex items-center justify-between gap-2">
                                            <div className="flex min-w-0 items-center gap-2">
                                                <AvatarInitial user={user} size="sm" />
                                                <div className="min-w-0">
                                                    <div className="truncate text-xs font-semibold text-grayscale-800 sm:text-sm">
                                                        {user.name}
                                                    </div>
                                                    {/* <div className="truncate text-xs text-grayscale-500">{user.username}</div> */}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeAssigneeMutation.mutate({
                                                        ticketId: selectedTicket.ticketId,
                                                        userId: user._id,
                                                    })
                                                }
                                                className="text-sm font-semibold text-grayscale-500"
                                            >
                                                <CrossIcon height={10} />
                                            </button>
                                        </div>
                                    ))}

                                    <label className="flex items-center gap-2 rounded border border-dashed border-separator-color p-2 text-xs font-semibold text-primary sm:text-sm">
                                        <UserPlusIcon />
                                        <select
                                            value=""
                                            onChange={(event) => {
                                                if (event.target.value) {
                                                    addAssigneeMutation.mutate({
                                                        ticketId: selectedTicket.ticketId,
                                                        userId: event.target.value,
                                                    });
                                                }
                                            }}
                                            className="min-w-0 flex-1 bg-transparent outline-none"
                                        >
                                            <option value="">Add Assignee</option>
                                            {availableAssignees.map((user) => (
                                                <option key={user._id} value={user._id}>
                                                    {user.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </DetailSection>

                                {/* <DetailSection title="Due Date">
                                    <input
                                        type="date"
                                        value={selectedTicket.dueDate?.slice(0, 10) || ''}
                                        onChange={(event) =>
                                            updateDetailsMutation.mutate({
                                                ticketId: selectedTicket.ticketId,
                                                payload: { dueDate: event.target.value || undefined },
                                            })
                                        }
                                        className="h-10 w-full rounded border border-separator-color px-3 text-sm outline-none focus:border-primary"
                                    />
                                </DetailSection> */}
                            </aside>
                        </>
                    )}
                </>
            ) : null}

            <TicketFormDrawer
                open={isCreateOpen}
                title="New Ticket"
                values={newTicket}
                users={assignableUsers}
                isSubmitting={createTicketMutation.isLoading}
                isLoadingUsers={assignableUsersQuery.isLoading}
                onClose={() => setIsCreateOpen(false)}
                onChange={setNewTicket}
                onSubmit={handleCreateTicket}
            />

            <TicketFormDrawer
                open={isEditOpen}
                title="Edit Ticket"
                values={editTicket}
                users={assignableUsers}
                isSubmitting={updateDetailsMutation.isLoading}
                isLoadingUsers={assignableUsersQuery.isLoading}
                onClose={() => setIsEditOpen(false)}
                onChange={setEditTicket}
                onSubmit={handleEditTicket}
                editMode
            />

            <Drawer open={isDetailsOpen} onClose={() => setIsDetailsOpen(false)} className="sm:!w-full md:!w-[500px]">
                <h3 className="text-lg font-semibold text-grayscale-900">Ticket Details</h3>

                {selectedTicket && (
                    <div className="mt-6 space-y-4">
                        <DetailRow label="Ticket ID" value={selectedTicket.ticketId} />
                        <DetailRow label="Title" value={selectedTicket.title} />
                        <DetailRow label="Description" value={selectedTicket.description} />
                        <div>
                            <div className="text-xs font-semibold text-grayscale-500">Status</div>
                            <div className="mt-1">
                                <Pill className={statusClass[selectedTicket.status]}>
                                    {formatStatus(selectedTicket.status)}
                                </Pill>
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-grayscale-500">Priority</div>
                            <div className="mt-1">
                                <Pill className={priorityClass[selectedTicket.priority]}>
                                    {formatPriority(selectedTicket.priority)}
                                </Pill>
                            </div>
                        </div>
                        <DetailRow label="Due Date" value={formatShortDate(selectedTicket.dueDate)} />
                        <DetailRow label="Created by" value={selectedTicket.createdBy?.name || 'Unknown'} />
                        <DetailRow label="Created" value={formatDateTime(selectedTicket.createdAt)} />
                        <DetailRow label="Last updated" value={formatDateTime(selectedTicket.updatedAt)} />

                        {enrichedAssignees.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-grayscale-700 sm:text-sm">Assignees</h4>
                                <div className="mt-2 space-y-2">
                                    {enrichedAssignees.map((user) => (
                                        <div key={user._id} className="flex items-center gap-2">
                                            <AvatarInitial user={user} size="sm" />
                                            <div className="min-w-0">
                                                <div className="text-xs font-semibold text-grayscale-800 sm:text-sm">
                                                    {user.name}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Drawer>
        </div>
    );
};

const Pill = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <span className={twMerge('inline-flex items-center rounded border px-2 py-1 text-xs font-semibold', className)}>
        {children}
    </span>
);

const AvatarInitial = ({ user, size = 'md' }: { user?: InternalSupportUser; size?: 'sm' | 'md' }) => (
    <div
        title={user?.name}
        className={twMerge(
            'grid shrink-0 place-items-center rounded-full bg-primary text-white font-semibold',
            size === 'sm' ? 'h-7 w-7 text-[11px]' : 'h-9 w-9 text-sm'
        )}
    >
        {user?.name
            ?.split(' ')
            .map((part) => part[0])
            .slice(0, 2)
            .join('') || 'U'}
    </div>
);

const AttachmentCard = ({ attachment }: { attachment: TicketAttachment }) => (
    <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-between gap-3 rounded border border-separator-color bg-white p-3 text-sm hover:border-primary"
    >
        <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded bg-grayscale-100 text-grayscale-600">
                <FileIcon />
            </div>
            <div className="min-w-0">
                <div className="truncate font-semibold text-grayscale-900">{attachment.name}</div>
                <div className="text-xs text-grayscale-500">
                    {formatFileSize(attachment.size)} · {attachment.mimeType || 'File'}
                </div>
            </div>
        </div>
        <DownloadIcon className="shrink-0 text-grayscale-500" />
    </a>
);

const MetaBlock = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="min-w-0">
        <div className="mb-1 text-xs font-semibold text-grayscale-500">{label}</div>
        <div className="min-h-[32px]">{children}</div>
    </div>
);

const renderMessageWithMentions = (text: string, mentions?: InternalSupportUser[]) => {
    if (!mentions || mentions.length === 0) {
        return text;
    }

    let lastIndex = 0;
    const parts: (string | JSX.Element)[] = [];

    for (const mention of mentions) {
        const displayName = mention.username || mention.name;
        const mentionStr = '@' + displayName;
        const index = text.indexOf(mentionStr, lastIndex);

        if (index !== -1) {
            if (index > lastIndex) {
                parts.push(text.substring(lastIndex, index));
            }
            parts.push(
                <span key={`${mention._id}-${index}`} className="font-semibold text-primary">
                    {mentionStr}
                </span>
            );
            lastIndex = index + mentionStr.length;
        }
    }

    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
};

const MessageList = ({
    messages,
    emptyText,
    currentUserId,
}: {
    messages: InternalSupportMessage[];
    emptyText: string;
    currentUserId?: string;
}) => {
    if (!messages.length) {
        return (
            <div className="rounded border border-separator-color bg-white p-6 text-sm text-grayscale-500">
                {emptyText}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {messages.map((message) => {
                const isSystem = message.type === 'SYSTEM';

                if (isSystem) {
                    return (
                        <div
                            key={message._id}
                            className="rounded border border-separator-color bg-white px-4 py-3 text-sm font-semibold text-grayscale-600"
                        >
                            {message.message}
                            <div className="mt-1 text-xs font-normal text-grayscale-500">
                                {formatDateTime(message.createdAt)}
                            </div>
                        </div>
                    );
                }

                const isCurrentUser = message.sender?._id === currentUserId;

                return (
                    <div key={message._id} className={twMerge('flex gap-3', isCurrentUser && 'flex-row-reverse')}>
                        <AvatarInitial user={message.sender} />
                        <article
                            className={twMerge(
                                'max-w-xs rounded-lg p-3',
                                isCurrentUser
                                    ? 'bg-grayscale-100 text-grayscale-900 rounded-tr-none'
                                    : 'bg-grayscale-100 text-grayscale-900 rounded-tl-none'
                            )}
                        >
                            {/* {!isCurrentUser && ( */}
                            <div className="text-xs font-semibold mb-1 capitalize">
                                {message.sender?.name || 'Unknown user'}
                            </div>

                            <p className="whitespace-pre-wrap text-sm text-grayscale-800 font-medium leading-5">
                                {renderMessageWithMentions(message.message, message.mentions)}
                            </p>
                            {!!message.attachments?.length && (
                                <div className="mt-2 grid gap-2">
                                    {message.attachments.map((attachment) => (
                                        <AttachmentCard
                                            key={`${message._id}-${attachment.name}-${attachment.url}`}
                                            attachment={attachment}
                                        />
                                    ))}
                                </div>
                            )}
                            <div className={twMerge('text-xs mt-1 text-grayscale-500')}>
                                {formatDateTime(message.createdAt)}
                            </div>
                        </article>
                    </div>
                );
            })}
        </div>
    );
};

const ActivityList = ({ activities }: { activities: InternalSupportActivity[] }) => {
    if (!activities.length) {
        return (
            <div className="rounded border border-separator-color bg-white p-6 text-sm text-grayscale-500">
                No activity yet.
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {activities.map((activity) => (
                <div key={activity._id} className="flex gap-3 rounded border border-separator-color bg-white p-3">
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-grayscale-100 text-grayscale-500">
                        <ClockIcon />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-grayscale-800">{activityText(activity)}</div>
                        <div className="mt-1 text-xs text-grayscale-500">{formatDateTime(activity.createdAt)}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const activityText = (activity: InternalSupportActivity) => {
    const actor = activity.performedBy?.name || 'System';

    switch (activity.action) {
        case 'TICKET_CREATED':
            return `${actor} created this ticket`;
        case 'ASSIGNEE_ADDED':
            return `${actor} added an assignee`;
        case 'ASSIGNEE_REMOVED':
            return `${actor} removed an assignee`;
        case 'STATUS_CHANGED':
        case 'TICKET_RESOLVED':
        case 'TICKET_REOPENED':
        case 'TICKET_CLOSED':
            return `${actor} changed status from ${activity.data?.oldValue} to ${activity.data?.newValue}`;
        case 'PRIORITY_CHANGED':
            return `${actor} changed priority from ${activity.data?.oldValue} to ${activity.data?.newValue}`;
        case 'CATEGORY_CHANGED':
            return `${actor} changed the category`;
        case 'DUE_DATE_CHANGED':
            return `${actor} changed the due date`;
        case 'ATTACHMENT_ADDED':
            return `${actor} added an attachment`;
        case 'MESSAGE_ADDED':
            return `${actor} added a message`;
        default:
            return `${actor} updated this ticket`;
    }
};

const DetailSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="mb-4 rounded border border-separator-color bg-white">
        <div className="border-b border-separator-color px-4 py-3 text-sm font-semibold text-grayscale-900">
            {title}
        </div>
        <div className="grid gap-3 p-4">{children}</div>
    </section>
);

const DetailRow = ({ label, value }: { label: string; value: string }) => (
    <div>
        <div className="text-xs font-semibold text-grayscale-500">{label}</div>
        <div className="mt-1 break-words text-sm font-semibold text-grayscale-800">{value}</div>
    </div>
);

interface TicketFormDrawerProps {
    open: boolean;
    title: string;
    values: CreateInternalSupportTicketPayload | UpdateInternalSupportTicketPayload;
    users: InternalSupportUser[];
    isSubmitting: boolean;
    isLoadingUsers: boolean;
    onClose: () => void;
    onChange: (values: any) => void;
    onSubmit: (event: FormEvent) => void;
    editMode?: boolean;
}

const TicketFormDrawer = (props: TicketFormDrawerProps) => {
    const { open, title, values, users, isSubmitting, isLoadingUsers, onClose, onChange, onSubmit, editMode } = props;
    const createValues = values as CreateInternalSupportTicketPayload;
    const attachmentFiles = createValues.attachmentFiles || [];
    const [isDragging, setIsDragging] = useState(false);

    const handleRemoveAttachment = (index: number) => {
        onChange({
            ...values,
            attachmentFiles: attachmentFiles.filter((_, currentIndex) => currentIndex !== index),
        });
    };

    const handleFilesDrop = (droppedFiles: File[]) => {
        if (droppedFiles.length) {
            onChange({
                ...values,
                attachmentFiles: [...attachmentFiles, ...droppedFiles],
            });
        }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        const files = Array.from(event.dataTransfer?.files || []);
        handleFilesDrop(files);
    };

    return (
        <Drawer open={open} onClose={onClose} className="md:!w-[560px]">
            <form onSubmit={onSubmit}>
                <h3 className="pr-8 text-base font-semibold text-grayscale-900 sm:text-lg">{title}</h3>

                <div className="mt-4 grid gap-3 sm:mt-6 sm:gap-4">
                    <label className="grid gap-1 text-xs font-semibold text-grayscale-700 sm:text-sm">
                        Title *
                        <input
                            value={values.title || ''}
                            onChange={(event) => onChange({ ...values, title: event.target.value })}
                            className="h-9 rounded border border-separator-color px-3 text-sm font-normal outline-none focus:border-primary sm:h-10"
                        />
                    </label>

                    <label className="grid gap-1 text-xs font-semibold text-grayscale-700 sm:text-sm">
                        Description
                        <textarea
                            value={values.description || ''}
                            onChange={(event) => onChange({ ...values, description: event.target.value })}
                            className="min-h-[100px] rounded border border-separator-color p-2 text-sm font-normal outline-none focus:border-primary sm:min-h-[120px] sm:p-3"
                        />
                    </label>

                    <label className="grid gap-1 text-xs font-semibold text-grayscale-700 sm:text-sm">
                        Priority
                        <select
                            value={values.priority || 'MEDIUM'}
                            onChange={(event) =>
                                onChange({ ...values, priority: event.target.value as TicketPriority })
                            }
                            className="h-9 rounded border border-separator-color px-3 text-sm font-normal outline-none focus:border-primary sm:h-10"
                        >
                            {ticketPriorities.map((priority) => (
                                <option key={priority} value={priority}>
                                    {formatPriority(priority)}
                                </option>
                            ))}
                        </select>
                    </label>

                    {editMode && (
                        <label className="grid gap-1 text-xs font-semibold text-grayscale-700 sm:text-sm">
                            Status
                            <select
                                value={values.status || 'OPEN'}
                                onChange={(event) =>
                                    onChange({ ...values, status: event.target.value as TicketStatus })
                                }
                                className="h-9 rounded border border-separator-color px-3 text-sm font-normal outline-none focus:border-primary sm:h-10"
                            >
                                {ticketStatuses.map((status) => (
                                    <option key={status} value={status}>
                                        {formatStatus(status)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    <label className="grid gap-1 text-xs font-semibold text-grayscale-700 sm:text-sm">
                        Due Date
                        <input
                            type="date"
                            value={values.dueDate?.slice(0, 10) || ''}
                            onChange={(event) => onChange({ ...values, dueDate: event.target.value })}
                            className="h-9 rounded border border-separator-color px-3 text-sm font-normal outline-none focus:border-primary sm:h-10"
                        />
                    </label>

                    <div className="grid gap-2 text-xs font-semibold text-grayscale-700 sm:text-sm">
                        Assignees
                        <div className="max-h-[200px] overflow-y-auto rounded border border-separator-color sm:max-h-[220px]">
                            {isLoadingUsers && (
                                <div className="flex justify-center p-3 sm:p-4">
                                    <CircularProgress size={18} />
                                </div>
                            )}

                            {!isLoadingUsers && !users.length && (
                                <div className="p-3 text-xs font-normal text-grayscale-500 sm:p-4 sm:text-sm">
                                    No users found.
                                </div>
                            )}

                            {users.map((user) => {
                                const selected = values.assignees?.includes(user._id) || false;

                                return (
                                    <label
                                        key={user._id}
                                        className="flex cursor-pointer items-center gap-3 border-b border-separator-color px-3 py-2 last:border-b-0"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            onChange={(event) => {
                                                const currentAssignees = createValues.assignees || [];

                                                onChange({
                                                    ...values,
                                                    assignees: event.target.checked
                                                        ? [...currentAssignees, user._id]
                                                        : currentAssignees.filter((userId) => userId !== user._id),
                                                });
                                            }}
                                        />
                                        <AvatarInitial user={user} size="sm" />
                                        <div className="min-w-0">
                                            <div className="truncate text-sm font-semibold text-grayscale-800">
                                                {user.name}
                                            </div>
                                            <div className="truncate text-xs font-normal text-grayscale-500">
                                                {user.username}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {!editMode && (
                        <div className="grid gap-2">
                            <div className="text-sm font-semibold text-grayscale-700">Attachments</div>
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                className={twMerge(
                                    'grid cursor-pointer place-items-center rounded border-2 border-dashed p-5 text-center transition',
                                    isDragging
                                        ? 'border-primary bg-primary/5'
                                        : 'border-grayscale-300 bg-background-color'
                                )}
                            >
                                <label className="grid w-full cursor-pointer place-items-center">
                                    <input
                                        type="file"
                                        multiple
                                        className="hidden"
                                        onChange={(event) => {
                                            const files = Array.from(event.target.files || []);
                                            handleFilesDrop(files);
                                            event.target.value = '';
                                        }}
                                    />
                                    <UploadIcon className="mb-2 text-primary" />
                                    <div className="text-sm font-semibold text-primary">
                                        Drag and drop files or click to upload
                                    </div>
                                    <div className="mt-1 text-xs font-normal text-grayscale-500">
                                        Files will be submitted with the ticket.
                                    </div>
                                </label>
                            </div>

                            {attachmentFiles.map((file, index) => (
                                <div
                                    key={`${file.name}-${file.lastModified}-${index}`}
                                    className="flex items-center justify-between gap-3 rounded border border-separator-color p-3"
                                >
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-semibold text-grayscale-800">
                                            {file.name}
                                        </div>
                                        <div className="text-xs font-normal text-grayscale-500">
                                            {formatFileSize(file.size)} · {file.type || 'File'}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveAttachment(index)}
                                        className="text-xs font-semibold text-danger"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-4 flex justify-end gap-2 border-t border-separator-color pt-3 sm:mt-6 sm:gap-3 sm:pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 rounded border border-separator-color px-3 text-xs font-semibold text-grayscale-700 sm:h-10 sm:px-4 sm:text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="h-9 rounded bg-primary px-3 text-xs font-semibold text-white disabled:opacity-50 sm:h-10 sm:px-4 sm:text-sm"
                    >
                        {isSubmitting ? (
                            <CircularProgress size={16} className="text-white" />
                        ) : editMode ? (
                            'Save'
                        ) : (
                            'Create'
                        )}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default InternalSupport;
